import { notification } from 'antd';
import {
    StageGroup,
    StageInfo,
    GroupedStage
} from '../types/stageTypes';
import {
    DefaultStageMapping,
    bulkUpdateDefaultStageMappings,
    forceSync
} from '../../../api/integrations';

/**
 * Prepares stage mappings for saving to the API
 * @param stageGroups Current stage groups
 * @returns Array of partial stage mappings ready for API submission
 */
export const prepareMappingsForSave = (stageGroups: StageGroup[]): Partial<DefaultStageMapping>[] => {
    // Get ALL stages from all groups
    const allStages: GroupedStage[] = [];
    stageGroups.forEach(group => {
        group.stages.forEach(stage => {
            allStages.push(stage);
        });
    });

    // Only include stages that are explicitly mapped or marked as "Always Unmapped"
    // This matches the logic from the original implementation
    const stagesToSave = allStages.filter(stage =>
        // Include if stage has been mapped to a specific type
        (stage.mapping.stage_type && !stage.mapping.is_unmapped) ||
        // OR include if stage has been explicitly marked as "Always Unmapped"
        stage.mapping.is_unmapped === true
    );

    // Find all interview stages and resequence their orders to ensure no gaps
    const interviewStages = stagesToSave.filter(stage =>
        stage.mapping.stage_type === 'INTERVIEW' && !stage.mapping.is_unmapped
    );

    // Sort by current order
    interviewStages.sort((a, b) => (a.mapping.order || 0) - (b.mapping.order || 0));

    // Reassign sequential order values
    interviewStages.forEach((stage, index) => {
        stage.mapping.order = index;
    });

    // Create mappings for saving
    return stagesToSave.map(stage => ({
        // Include only fields expected by DefaultStageMapping
        provider: stage.mapping.provider,
        external_name: stage.mapping.external_name,
        external_stage_name: stage.mapping.external_stage_name || stage.name,
        stage_type: stage.mapping.stage_type,
        order: stage.mapping.order ?? 0,
        is_unmapped: stage.mapping.is_unmapped || false,
        id: stage.mapping.id // Use the mapping ID for saving
    }));
};

/**
 * Saves stage mappings to the backend
 * @param mappings Array of stage mappings to save
 * @returns Promise that resolves when the save is complete
 */
export const saveStagesMappings = async (mappings: Partial<DefaultStageMapping>[]): Promise<void> => {
    try {
        // Send to API
        await bulkUpdateDefaultStageMappings(mappings);

        // Force sync to apply changes
        await forceSync();

        notification.success({
            message: 'Success',
            description: 'Default stage mappings were successfully saved.',
        });
    } catch (error) {
        console.error('Error saving stage mappings:', error);
        notification.error({
            message: 'Error',
            description: 'There was an error saving the stage mappings.',
        });
        throw error;
    }
};

/**
 * Generates a fresh set of stage groups from the original data
 * @param originalStages Original stage data
 * @param groupingFunction Function to group stages by type
 * @returns Freshly grouped stages with original data
 */
export const resetToOriginalStages = (
    originalStages: StageInfo[],
    groupingFunction: (stages: StageInfo[]) => StageGroup[]
): StageGroup[] => {
    // Create a deep copy of the original stages to avoid mutation
    const resetStages = originalStages.map(stage => ({
        ...stage,
        isChanged: false, // Reset the change flag
        mapping: { ...stage.mapping }
    }));

    // Group the reset stages
    return groupingFunction(resetStages);
}; 